import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Copy, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { API_URL } from "../../config.js";

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
});

export default function TwoFactorAuthentication({ compact = false, targetUserId }) {
  const [status, setStatus] = useState("loading");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (!targetUserId) return;
    axios.get(`${API_URL}/api/admin/users/${targetUserId}/2fa/status`, authConfig())
      .then(({ data }) => setStatus(data.enabled ? "enabled" : "idle"))
      .catch(() => {
        setStatus("idle");
        setError("Nu am putut verifica starea autentificării în doi pași.");
      });
  }, [targetUserId]);

  const startSetup = async () => {
    setStatus("starting");
    setError("");
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/users/${targetUserId}/2fa/setup`, {}, authConfig());
      setSetup(data);
      setStatus("setup");
      requestAnimationFrame(() => codeRef.current?.focus());
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Configurarea nu a putut fi pornită.");
      setStatus("idle");
    }
  };

  const confirmSetup = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Introdu un cod valid din 6 cifre.");
      return;
    }
    setStatus("confirming");
    setError("");
    try {
      await axios.post(`${API_URL}/api/admin/users/${targetUserId}/2fa/confirm`, { code }, authConfig());
      setSetup(null);
      setCode("");
      setStatus("enabled");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Codul nu a putut fi verificat.");
      setStatus("setup");
      codeRef.current?.focus();
      codeRef.current?.select();
    }
  };

  const copyKey = async () => {
    await navigator.clipboard.writeText(setup.manualKey);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const disableTwoFactor = async () => {
    setStatus("disabling");
    setError("");
    try {
      await axios.post(`${API_URL}/api/admin/users/${targetUserId}/2fa/disable`, {}, authConfig());
      setShowDisableConfirmation(false);
      setStatus("idle");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Autentificarea în doi pași nu a putut fi dezactivată.");
      setStatus("enabled");
    }
  };

  return (
    <section className={compact
      ? "relative z-20 flex min-h-10 shrink-0 items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 shadow-sm dark:bg-gray-800"
      : "w-full rounded-lg bg-white px-5 py-4 shadow-sm dark:bg-gray-800"} aria-labelledby="two-factor-title">
      <div className={`${compact ? "items-center gap-2" : "items-start gap-3"} flex`}>
        <span className={`${compact ? "h-7 w-7" : "h-9 w-9"} flex shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300`}>
          <ShieldCheck size={19} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="two-factor-title" className={`${compact ? "text-sm leading-none" : "text-base"} font-semibold text-gray-900 dark:text-gray-100`}>{compact ? "2FA" : "Securitatea contului"}</h2>
            {status === "enabled" && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">Activă</span>}
          </div>
          <p className={`${compact ? "hidden" : "mt-1"} max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300`}>
            Autentificare în doi pași prin Microsoft Authenticator, Google Authenticator sau altă aplicație TOTP.
          </p>
        </div>
      </div>

      {status === "loading" && <div className={`${compact ? "h-7 w-20" : "mt-5 h-10 w-64"} animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700`} aria-label="Se încarcă" />}

      {!compact && (status === "enabled" || status === "disabling") && (
        <div className="mt-5">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={18} aria-hidden="true" /> Activată. Contul solicită un cod la fiecare autentificare.
          </div>
          {!showDisableConfirmation ? (
            <button type="button" onClick={() => setShowDisableConfirmation(true)}
              className="mt-4 rounded-lg px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-red-300 dark:hover:bg-red-500/10">
              Doriți să dezactivați?
            </button>
          ) : (
            <div className="mt-4 rounded-lg bg-red-50 p-4 dark:bg-red-500/10">
              <p className="text-sm font-medium text-red-900 dark:text-red-200">După dezactivare, contul va putea fi accesat doar cu parola. Continuați?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={disableTwoFactor} disabled={status === "disabling"}
                  className="inline-flex min-h-9 items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-60 dark:focus:ring-offset-gray-800">
                  {status === "disabling" && <Loader2 className="animate-spin" size={16} />} Dezactivează
                </button>
                <button type="button" onClick={() => setShowDisableConfirmation(false)} disabled={status === "disabling"}
                  className="min-h-9 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-60 dark:text-gray-200 dark:hover:bg-gray-800">
                  Anulează
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {compact && (status === "enabled" || status === "disabling") && (
        <div>
          <button type="button" onClick={() => setShowDisableConfirmation((value) => !value)}
            className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:text-emerald-300 dark:hover:bg-emerald-500/10">
            Activată
          </button>
          {showDisableConfirmation && (
            <div className="absolute right-0 top-12 w-72 rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Doriți să dezactivați autentificarea în doi pași?</p>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={disableTwoFactor} disabled={status === "disabling"} className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">Dezactivează</button>
                <button type="button" onClick={() => setShowDisableConfirmation(false)} className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700">Anulează</button>
              </div>
            </div>
          )}
        </div>
      )}

      {(status === "idle" || status === "starting") && (
        <button type="button" onClick={startSetup} disabled={status === "starting"}
          className={`${compact ? "min-h-7 px-2.5 py-1 text-xs" : "mt-5 min-h-10 px-4 py-2 text-sm"} inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:ring-offset-gray-800`}>
          {status === "starting" ? <Loader2 className="animate-spin" size={17} /> : <KeyRound size={17} />}
          {status === "starting" ? "Se pregătește…" : compact ? "Configurează" : "Adaugă autentificarea în 2 pași"}
        </button>
      )}

      {(status === "setup" || status === "confirming") && setup && (
        <div className={compact
          ? "absolute right-0 top-12 w-[min(90vw,720px)] rounded-lg bg-white p-5 shadow-lg dark:bg-gray-800"
          : "mt-6 border-t border-gray-200 pt-6 dark:border-gray-700"}>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="shrink-0 rounded-lg bg-white p-3 ring-1 ring-gray-200">
              <img src={setup.qrCode} alt="Cod QR pentru configurarea aplicației de autentificare" className="h-52 w-52" />
            </div>
            <div className="max-w-md flex-1">
              <ol className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
                <li><strong className="text-gray-900 dark:text-gray-100">1.</strong> Deschide aplicația de autentificare și alege adăugarea unui cont.</li>
                <li><strong className="text-gray-900 dark:text-gray-100">2.</strong> Scanează codul QR.</li>
                <li><strong className="text-gray-900 dark:text-gray-100">3.</strong> Introdu mai jos codul afișat în aplicație.</li>
              </ol>
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Nu poți scana codul?</p>
                <button type="button" onClick={copyKey} className="mt-1 inline-flex max-w-full items-center gap-2 rounded-md px-2 py-1 font-mono text-xs text-violet-700 hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:text-violet-300 dark:hover:bg-violet-500/10">
                  <span className="truncate">{setup.manualKey}</span><Copy size={14} /> {copied && <span className="font-sans">Copiat</span>}
                </button>
              </div>
              <form onSubmit={confirmSetup} className="mt-5">
                <label htmlFor="two-factor-code" className="block text-sm font-medium text-gray-800 dark:text-gray-200">Cod de verificare</label>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <input ref={codeRef} id="two-factor-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000"
                    className="w-full rounded-lg border-gray-300 bg-white text-center font-mono text-lg tracking-[0.25em] text-gray-900 focus:border-violet-500 focus:ring-violet-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 sm:w-40" />
                  <button disabled={status === "confirming" || code.length !== 6} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:ring-offset-gray-800">
                    {status === "confirming" && <Loader2 className="animate-spin" size={17} />} Confirmă și activează
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {error && <p role="alert" className={compact
        ? "absolute right-0 top-12 w-72 rounded-lg bg-white p-3 text-sm font-medium text-red-700 shadow-lg dark:bg-gray-800 dark:text-red-300"
        : "mt-4 text-sm font-medium text-red-700 dark:text-red-300"}>{error}</p>}
    </section>
  );
}
